import {
  isAssetReadOnly,
  Media,
  PermissionValues,
  useCurrentAccount,
  useProduct,
  useWorkspaceAsset,
  WorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AddIcon,
  Button,
  CardButton,
  CategoryFilter,
  Colors,
  Layout,
  SlideAction,
  tokens,
  useSlideLayout,
} from '@gripp/shared-ui';
import { smallPadding } from 'libs/shared-ui/src/styles/spacing';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { Modal, Portal, Text } from 'react-native-paper';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAssetRouting } from '../assetRouteContext';
import { AddFilesLinksButton } from './addFilesLinksButton';
import { AssetFileItem } from './assetFileItem';
import { ViewAllSectionHeader } from './viewAllSectionHeader';

type AssetMediaFoldProps = {
  workspaceAsset: WorkspaceAsset | undefined;
  assetId: string;
  forceShowing?: boolean;
};
type MediaExtended = Media & { isFromProduct?: boolean };

export const AssetMediaFold: React.FC<AssetMediaFoldProps> = ({
  workspaceAsset,
  assetId,
  forceShowing,
}) => {
  const { issueId } = useParams();
  const location = useLocation();
  const isOnAssetPage = location.pathname.startsWith('/assets/');
  const { t } = useTranslation();
  const mediaData = useMemo(
    () => workspaceAsset?.asset?.media || [],
    [workspaceAsset?.asset?.media]
  );
  const { account } = useCurrentAccount();
  const isReadOnlyMode = isAssetReadOnly(account, workspaceAsset);
  const [assetMediaModalOpened, setAssetMediaModalOpened] = useState(false);
  const navigate = useNavigate();
  const { onSlideChange } = useSlideLayout();
  const { basePath } = useAssetRouting(assetId);
  const isFilesPage = location.pathname.includes('/files');

  const openAssetMediaModal = () => {
    setAssetMediaModalOpened(true);
  };

  const handleFilePress = useCallback(
    async (fileId: string) => {
      await onSlideChange(SlideAction.NEXT);
      navigate(`${basePath}/files/${fileId}`);
    },
    [navigate, basePath, onSlideChange]
  );

  const handleViewAllFiles = useCallback(async () => {
    if (!isFilesPage) {
      await onSlideChange(SlideAction.NEXT);
      navigate(`${basePath}/files`);
    }
  }, [navigate, basePath, onSlideChange, isFilesPage]);

  if (!workspaceAsset || (!forceShowing && issueId && isOnAssetPage)) return null;

  return (
    <View
      style={[
        styles.containerHeightCropCircle,
        !mediaData.length
          ? { height: 100 }
          : { height: mediaData.length * 60 + 32 },
      ]}
    >
      <ViewAllSectionHeader
        title={t('asset.files.title')}
        onPress={handleViewAllFiles}
      />
      {mediaData.length > 0 ? (
        <View style={[styles.listContainer]}>
          <FlatList
            data={mediaData}
            keyExtractor={(item, index) =>
              item.id?.toString() || index.toString()
            }
            scrollEnabled={false}
            renderItem={({ item }) => (
              <View style={styles.cardButtonContainer}>
                <CardButton
                  text={item.name}
                  color="white"
                  leftIcon="Document B"
                  rightIcon="Chevron Right"
                  showAvatars={false}
                  onPress={item.id ? () => handleFilePress(item.id!) : undefined}
                  bordered={false}
                />
              </View>
            )}
            showsVerticalScrollIndicator={true}
          />
        </View>
      ) : isReadOnlyMode ? null : (
        <Button
          style={styles.addFileEmpty}
          onPress={openAssetMediaModal}
          leftIcon={<AddIcon />}
          variant={'rectangular'}
        >
          {t('asset.files.addFile')}
        </Button>
      )}
      <ModalAssetMedia
        isOpened={assetMediaModalOpened}
        onCancel={() => setAssetMediaModalOpened(false)}
        assetId={assetId}
      />
    </View>
  );
};

// TODO: Crop Circle Redesign | Remove after feature is fully rolled out
const ModalAssetMedia: React.FC<{
  isOpened: boolean;
  onCancel: () => void;
  assetId: string;
}> = (props) => {
  const { t } = useTranslation();
  const { workspaceAsset, invalidateQuery } = useWorkspaceAsset(props.assetId);
  const asset = workspaceAsset?.asset;
  const { account } = useCurrentAccount();
  const canEdit = account?.hasPermission(PermissionValues.asset.update);
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    string | undefined
  >(undefined);

  const { product } = useProduct(asset?.product?.id || '');

  const allFiles = useMemo(() => {
    const files: MediaExtended[] = [];

    const productMediaIds = new Set(
      product?.media?.map((m: Media) => m.id) || []
    );

    // Add all asset files and mark which ones are from the product
    if (asset?.media) {
      asset.media.forEach((media: Media) => {
        const isFromProduct = productMediaIds.has(media.id);
        files.push({ ...media, isFromProduct });
      });
    }

    return files;
  }, [asset?.media, product?.media]);

  // Filter files based on selected category
  const filteredFiles = useMemo(() => {
    if (!selectedCategoryId) {
      return allFiles;
    }

    return allFiles.filter((file) =>
      file.categories?.some((category) => category.id === selectedCategoryId)
    );
  }, [allFiles, selectedCategoryId]);
  const isEmptyAssetFiles = filteredFiles.length === 0;

  const renderFiles = () => {
    return (
      <View style={[styles.fileContainer, { maxHeight: 430 }]}>
        <CategoryFilter
          allFiles={allFiles}
          selectedCategoryId={selectedCategoryId}
          onCategorySelect={setSelectedCategoryId}
        />
        <ScrollView showsVerticalScrollIndicator={true}>
          {filteredFiles.map((file: MediaExtended) => (
            <AssetFileItem
              key={file.id}
              asset={asset}
              media={file}
              invalidateQuery={invalidateQuery}
              isFromProduct={file.isFromProduct}
              disabled={!canEdit}
            />
          ))}
        </ScrollView>
      </View>
    );
  };

  return (
    <Portal>
      <Modal
        visible={props.isOpened}
        onDismiss={props.onCancel}
        contentContainerStyle={styles.modalContentContainer}
      >
        <View style={styles.containerModal}>
          <Text style={styles.cardHeader}>{t('asset.files.title')}</Text>
          <View style={isEmptyAssetFiles && styles.marginTop}>
            {isEmptyAssetFiles ? (
              <Text style={styles.formItemTitle}>
                {t('asset.files.noFiles')}
              </Text>
            ) : (
              renderFiles()
            )}
            {asset && (
              <AddFilesLinksButton
                asset={asset}
                invalidateQuery={invalidateQuery}
                disabled={!canEdit}
              />
            )}
          </View>
        </View>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modalContentContainer: {
    width: '100%',
    maxWidth: 560,
    maxHeight: 600,
    alignSelf: 'center',
    overflow: 'visible',
  },
  containerModal: {
    ...Layout.container,
    backgroundColor: tokens.colors.brand.white,
    borderRadius: tokens.borderRadius.xl,
    padding: tokens.spacing.md,
    overflow: 'visible',
  },
  containerHeightCropCircle: {
    maxHeight: 262,
    minHeight: 80,
    height: '100%',
    marginBottom: tokens.spacing.xl,
    paddingHorizontal: tokens.spacing.lg,
  },
  listContainer: {
    maxHeight: 184,
    overflow: 'hidden',
    marginBottom: 54,
  },
  addFileEmpty: {
    flex: 1,
    height: 56,
    maxHeight: 56,
    backgroundColor: tokens.colors.slate[100],
  },
  fileContainer: {
    flexDirection: 'column',
    gap: 10,
  },
  marginTop: {
    marginTop: 150,
  },
  cardButtonContainer: {
    marginBottom: tokens.spacing.sm,
  },
  cardHeader: {
    ...tokens.typography.title.mediumEmphasised,
    color: tokens.colors.neutral[800],
    paddingVertical: smallPadding.paddingVertical,
  },
  formItemTitle: {
    ...tokens.typography.body.mediumEmphasised,
    marginBottom: tokens.spacing.sm,
  },
});
